// Fee & Obligation PR-5 standing worksheet — read-only client for the loan's
// forward projection (the system's best projection of the next N statements, a
// read-side mirror of the orchestrator's sizing) and the Income Pools FORWARD
// overlay (expected-ahead, rendered beside the booked matrix).

import { api } from "@/api/client";
import type {
  IncomePoolColumnDto,
  IncomePoolsPeriodRowDto,
} from "@/api/portfolioAdmin/incomePoolsApi";

export interface ProjectedSizingDto {
  scheduledPayment: number;
  tpsPlatformFee: number;
  feeOnTop: number;
  phoenixSpread: number;
  brokerFee: number;
  sellerInterest: number;
  sellerPrincipal: number;
  /** The Payment Administration Fee (BLA Core §2.4) this sizing was computed
   *  with — the loan's rate of the scheduled P&I, GROSS.
   *
   *  🔴 IT IS NOT A CARVE AND MUST NOT BE RENDERED AS ONE. Unlike
   *  `buyerConsultingMonthlyFee` below, this is charged to the borrower ON TOP
   *  of the payment: the seller's legs go UP by exactly the servicer fee when
   *  it is live, not down (pinned by `PaymentAdministrationFeeTests
   *  .The_seller_stops_being_short_paid_by_exactly_the_servicer_fee`). The
   *  borrower-facing figure is rendered from the STATEMENT's own
   *  `paymentAdministrationFee` above the divider; this one is here only as the
   *  signal that the servicer fee was funded out of the fee rather than deducted
   *  from the payment — which is what `tpsPlatformFee` beside it stops meaning.
   *
   *  🔴 AND IT IS THE GROSS, WHICH CONTAINS `tpsPlatformFee`. The engine splits
   *  it into two legs — the servicer's fee at WF1 P1 and the remainder to
   *  Phoenix at WF1 P24 — so putting this figure in the recipient split beside
   *  the TPS row counts that fee twice ($25.00 + $144.75 of legs against
   *  $144.75 actually disbursed, on the repo's own fixture).
   *
   *  Optional so existing inline fixtures keep compiling; the server has sent
   *  it since #1062, which introduced the fee. (The sibling block below says
   *  #1135 and is right about ITS member — do not sync the two.) Read as
   *  `?? 0`. */
  paymentAdministrationFee?: number;
  /** The Buyer Consulting Fee's MONTHLY leg (WF1 P10) — a CARVE out of what
   *  would otherwise reach the seller, sized at the loan's rate times the full
   *  scheduled P&I. `sellerInterest` and `sellerPrincipal` above have ALREADY
   *  dropped by it, so without this line the split shows money gone and names
   *  nothing — the same failure the Financials seller schedule's own
   *  "Consulting fee" column exists to prevent, one surface over.
   *
   *  0 on a loan whose rate is 0.00 (the overwhelming majority) AND on a
   *  BALLOON period, where P10 does not fire and the liquidity leg (P11) bills
   *  the event instead. The liquidity leg has no counterpart here by
   *  construction: it is event-scoped and sized from the cash actually
   *  remitted, so there is no future period to attach it to.
   *
   *  Optional so existing inline fixtures keep compiling; the server has sent
   *  it since #1135. Read as `?? 0`. */
  buyerConsultingMonthlyFee?: number;
}

export interface ProjectedStatementDto {
  periodIndex: number;
  dueDate: string;
  /** Locked = settled actuals; else a forward projection. */
  isLocked: boolean;
  /** True once an actual bill has been issued for the period — a statement
   *  generated and sent to the servicer (through Paid). Such a period is no
   *  longer a pure projection ("Billed"), even before it locks/settles. */
  isBilled: boolean;
  /** The ledger period status when locked, or "projected". */
  status: string;
  amountDue: number;
  expectedInterest: number;
  expectedPrincipal: number;
  servicingFeeOnTop: number;
  /** The Payment Administration Fee (BLA Core §2.4) billed on this period —
   *  the ledger's figure, the sibling of `servicingFeeOnTop` above. Both are
   *  charged to the borrower ON TOP of the scheduled P&I and are already inside
   *  `amountDue`; the two are mutually exclusive, so at most one is non-zero.
   *
   *  This is the one to RENDER. `sizing.paymentAdministrationFee` answers a
   *  different question (what the sizing was computed with) and is null on a
   *  locked period, where this one still reports the bill.
   *
   *  Optional so existing inline fixtures keep compiling. Read as `?? 0`. */
  paymentAdministrationFee?: number;
  isBalloon: boolean;
  receivedAmount: number;
  priorBalanceCarried: number;
  creditApplied: number;
  /** The mirrored recipient sizing for a projected monthly period; null when
   *  locked or non-monthly. */
  sizing: ProjectedSizingDto | null;
}

export interface ProjectedObligationLineDto {
  obligationId: string;
  typeCode: string;
  typeDisplay: string;
  waterfallPriority: number | null;
  state: string;
  remaining: number;
}

export interface ProjectedFeeLineDto {
  feeId: string;
  feeType: string;
  amount: number;
  status: string;
}

export interface LoanForwardProjectionDto {
  loanId: string;
  loanNumber: string;
  isDistributionLoan: boolean;
  /** The next statement a new fee or obligation can still land on: the first
   *  period that is neither locked nor already BILLED, falling back to the
   *  first not-yet-locked period when every unlocked period in the horizon has
   *  had its bill issued. Null when every simulated period is locked.
   *  Server-derived — do NOT re-guard this on `isBilled` here; one source of
   *  truth beats two guards that can disagree. */
  nextProjectedIndex: number | null;
  periods: ProjectedStatementDto[];
  openObligations: ProjectedObligationLineDto[];
  approvedUnbilledFees: ProjectedFeeLineDto[];
  /** The loan's Buyer Consulting Fee carve rates, as FRACTIONS (0.02 = 2%) —
   *  loan-level TERMS, not per-period figures.
   *
   *  🔴 Do NOT re-derive "does this loan carry the fee" from the periods. A
   *  LOCKED period carries no `sizing`, and the BALLOON's monthly carve is 0 by
   *  rule, so `periods.some(p => p.sizing?.buyerConsultingMonthlyFee > 0)` reads
   *  FALSE on a fee-carrying loan whose only unlocked period is the balloon —
   *  precisely the period a reader needs explained.
   *
   *  Optional so existing inline fixtures keep compiling; read as `?? 0`. */
  buyerConsultingFeeMonthlyPct?: number;
  buyerConsultingFeeLiquidityPct?: number;
}

export interface LoanIncomePoolsForwardDto {
  loanId: string;
  loanNumber: string;
  pools: IncomePoolColumnDto[];
  periods: IncomePoolsPeriodRowDto[];
}

export const forwardProjectionApi = {
  get: (loanId: string, horizonMonths?: number) =>
    api.get<LoanForwardProjectionDto>(
      `/portfolio-admin/loans/${loanId}/forward-projection${horizonQuery(horizonMonths)}`,
    ),
  incomePoolsForward: (loanId: string, horizonMonths?: number) =>
    api.get<LoanIncomePoolsForwardDto>(
      `/portfolio-admin/loans/${loanId}/income-pools-forward${horizonQuery(horizonMonths)}`,
    ),
};

function horizonQuery(horizonMonths?: number): string {
  return horizonMonths ? `?horizonMonths=${horizonMonths}` : "";
}
